/**
 * 히트맵 패널의 표본 상한(스팬 건수). 초기 쿼리 정의(constants/queryBuilder),
 * 패널 전환 시 쿼리 시드(container/NewWidget/utils), 보충 표본 훅
 * (HeatmapPanel/useHeatmapSamples)이 모두 이 값을 참조한다.
 *
 * 의존 없는 leaf 모듈로 둔 이유: constants → container 순환 import를 만들지 않고
 * 세 소비처가 같은 값을 공유하기 위함이다.
 */
export const HEATMAP_SAMPLE_LIMIT = 2000;

/**
 * 히트맵에 그리는 오류 점의 최소 보장 건수. 오류 보충 쿼리는 창 전체 오류를
 * 상한(HEATMAP_SAMPLE_LIMIT)까지 전량 가져오지만, 그걸 균일 표본 위에 다 얹으면
 * 실제 1~3%인 오류가 화면에선 절반으로 보인다(2026-08-28 Azure 실측). 그래서
 * max(균일 표본 안의 오류 수, 이 값)까지만 그린다 — 비율은 정직하게, 드물 때만 최소
 * 이만큼은 보이게. [전체]·[오류만] 두 모드가 같은 오류 점 집합을 쓴다(오류만은 그
 * 집합에서 정상 점만 숨긴 것) — 토글했을 때 붉은 점이 새로 생기면 고객이 헷갈린다.
 * 범례 카운터·오류 '+' 표기는 여전히 전량 기준이다.
 */
export const HEATMAP_MIN_VISIBLE_ERRORS = 100;

/**
 * 느린 스팬 보충의 응답시간 계단. SCOUTER 에이전트의 XLog 계단식 샘플링
 * (xlog_sampling_step3_ms=3000 / step3_rate_pct=30 / over_rate_pct=100)을
 * «조회 시점»에 재현한 것이다.
 *
 * 왜 필요한가: 균일 표본(HEATMAP_SAMPLE_LIMIT)은 무작위라 «소수를 소수 그대로»
 * 깎는다. 표본율이 5%면 p99를 만드는 꼬리 15건이 화면엔 0.8개로 떨어져 지연 사고가
 * 통째로 사라진다(2026-08-31 Azure 시연 실측 — p99 패널은 4.4s를 찍는데 같은 필터의
 * 산점도엔 점이 없었다). 그래서 느린 구간만 따로 뽑아 얹는다. 오류 보충
 * (errorsOnlyQuery)이 오류에 대해 하는 일과 정확히 같은 대칭짝이다.
 *
 * 왜 백분위가 아니라 고정 절대값인가: SCOUTER(100ms/1s/3s)도 WhaTap
 * (profile_basetime=500ms)도 고정값을 쓴다. 백분위 기준은 전체가 느려지면 같이
 * 올라가서, 정작 사고가 났을 때 "느린 것"의 정의가 사고를 따라 도망간다.
 *
 * ratePct는 계단별 렌더 예산 비율(takeTierRate), limit은 쿼리 상한이다.
 * maxMs=null은 상한 없음 — 반열림 [minMs, maxMs)이라 경계값이 두 계단에 겹치지 않는다.
 */
export interface HeatmapSlowTier {
	minMs: number;
	maxMs: number | null;
	ratePct: number;
	limit: number;
}

export const HEATMAP_SLOW_TIERS: HeatmapSlowTier[] = [
	{ minMs: 1000, maxMs: 3000, ratePct: 30, limit: 1000 },
	{ minMs: 3000, maxMs: null, ratePct: 100, limit: 1000 },
];
